import {
  Component, Input, Output, EventEmitter,
  OnChanges, SimpleChanges, OnDestroy, inject
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Subscription } from "rxjs";
import { TranslateModule } from "@ngx-translate/core";
import { ExcellenceModalService } from "src/app/services/performance-service/excellence-modal.service";
import {
  RecognitionTop5ModalComponent,
  RecognitionTop5Unit,
} from "src/app/components/recognition-top5-modal/recognition-top5-modal.component";

export interface ExMonth {
  month: string;
  val: string;
}

export interface ExEngagementMonth {
  month: string;
  val: string;
}

export interface ExRecognitionItem {
  label: string;
  iconSrc: string;
  active: boolean;
  top5?: RecognitionTop5Unit[];
}

export interface ExAchievement {
  label: string;
  xp?: number;
  date: string;
  iconSrc: string;
}

@Component({
  selector: "app-excellence-modal",
  standalone: true,
  imports: [CommonModule, TranslateModule, RecognitionTop5ModalComponent],
  templateUrl: "./excellence-modal.component.html",
  styleUrls: ["./excellence-modal.component.scss"],
})
export class ExcellenceModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  private excellenceModalService = inject(ExcellenceModalService);
  private dataSub?: Subscription;

  // ── Header ──────────────────────────────────────────────────────────
  headerIconSrc = "assets/performance/excellence-award.svg";
  headerTitle   = "Excellence Awards";

  // ── XP Tiers ────────────────────────────────────────────────────────
  hseTierXp     = 0;
  perfRawPoints = 0;
  engRawPoints  = 0;

  get progressBarLabel(): string {
    const xp = this.hseTierXp;
    if (xp >= 30000) return 'Platinum reached!';
    if (xp >= 25000) return `${(30000 - xp).toLocaleString()} points to Platinum`;
    if (xp >= 20000) return `${(25000 - xp).toLocaleString()} points to Gold`;
    return `${(20000 - xp).toLocaleString()} points to Silver`;
  }

  get progressBarPointsText(): string {
    return `${(this.hseTierXp || 0).toLocaleString()} Points`;
  }

  get progressBarWidth(): string {
    const xp = this.hseTierXp;
    if (xp <= 0) return '0%';
    const capped = Math.min(xp, 30000);
    const raw = (capped / 30000) * 100;
    const pct = Math.min(100, Math.max(raw, 15));
    return pct.toFixed(1) + '%';
  }

  get progressBarGradient(): string {
    const xp = this.hseTierXp;
    if (xp <= 0) return '#ECE9E9';

    const perf  = this.perfRawPoints;
    const eng   = this.engRawPoints;
    const recog = Math.max(0, xp - (perf + eng));
    const total = perf + eng + recog;

    if (total <= 0) return '#305d7a';

    const MIN = 15;
    let perfPct  = perf  > 0 ? Math.max((perf  / total) * 100, MIN) : 0;
    let engPct   = eng   > 0 ? Math.max((eng   / total) * 100, MIN) : 0;
    let recogPct = recog > 0 ? Math.max((recog / total) * 100, MIN) : 0;

    const sum = perfPct + engPct + recogPct;
    perfPct  = (perfPct  / sum) * 100;
    engPct   = (engPct   / sum) * 100;
    recogPct = (recogPct / sum) * 100;

    const stops: string[] = [];
    let cursor = 0;
    if (perf  > 0) { stops.push(`#305d7a ${cursor}%, #305d7a ${cursor + perfPct}%`);  cursor += perfPct;  }
    if (eng   > 0) { stops.push(`#23a7cf ${cursor}%, #23a7cf ${cursor + engPct}%`);   cursor += engPct;   }
    if (recog > 0) { stops.push(`#007382 ${cursor}%, #007382 100%`); }

    return `linear-gradient(90deg, ${stops.join(', ')})`;
  }

  get progressBarDotColor(): string {
    const xp    = this.hseTierXp;
    const perf  = this.perfRawPoints;
    const eng   = this.engRawPoints;
    const recog = Math.max(0, xp - (perf + eng));
    if (recog > 0) return '#007382';
    if (eng   > 0) return '#23a7cf';
    return '#305d7a';
  }

  get showProgressDot(): boolean {
    return this.hseTierXp > 0 && this.hseTierXp < 30000;
  }

  get leftIcon(): string {
    const xp = this.hseTierXp;
    if (xp >= 25000) return 'assets/performance/unit-gold.svg';
    if (xp >= 20000) return 'assets/performance/unit-silver.svg';
    return '';
  }

  get rightIcon(): string {
    const xp = this.hseTierXp;
    if (xp >= 25000) return 'assets/performance/unit-platinum.svg';
    if (xp >= 20000) return 'assets/performance/unit-gold.svg';
    return 'assets/performance/unit-silver.svg';
  }

  get performanceScoreColor(): string {
    const score = this.performanceScore;
    if (score >= 900) return '#67319E'; // Exceptional - purple
    if (score >= 800) return '#009146'; // Very Good - dark green
    if (score >= 650) return '#8BB729'; // Good - light green
    if (score >= 500) return '#E8BF03'; // Fair - yellow
    return '#ED2224';                   // Poor - red
  }

  get engagementScoreColor(): string {
    const score = this.engagementScore;
    if (score >= 900) return '#67319E'; // Exceptional - purple
    if (score >= 800) return '#009146'; // Very Good - dark green
    if (score >= 650) return '#8BB729'; // Good - light green
    if (score >= 500) return '#E8BF03'; // Fair - yellow
    return '#ED2224';                   // Poor - red
  }

  // ── Performance ──────────────────────────────────────────────────────
  performanceCardHeader = { title: "Performance", iconSrc: "assets/performance/signal-tower.svg", bg: "#305D7A" };
  performanceScore      = 0;
  performanceTotalLabel = "Total: 0 points";
  performanceMonths: ExMonth[] = [];

  // ── Engagement ───────────────────────────────────────────────────────
  engagementCardHeader = { title: "Engagement", iconSrc: "assets/performance/handshake.svg", bg: "#23A7CF" };
  engagementScore      = 0;
  engagementTotalLabel = "Total: 0 points";
  engagementMonths: ExEngagementMonth[] = [];

  // ── Recognition ──────────────────────────────────────────────────────
  recognitionCardHeader = { title: "Recognition", iconSrc: "assets/performance/ribbon.svg", bg: "#007382" };
  recognitionItems: ExRecognitionItem[] = [];

  // ── Past Achievements ─────────────────────────────────────────────────
  achievements: ExAchievement[] = [];

  // ── Top 5 Popup ───────────────────────────────────────────────────────
  top5PopupOpen = false;
  top5PopupTitle = '';
  top5PopupIcon = '';
  top5PopupUnits: RecognitionTop5Unit[] = [];
  private apiTop5HseUnits: RecognitionTop5Unit[] = [];
  private selectedCountryFlag = '';

  openEngagedUnitsTop5(): void {
    this.top5PopupTitle = 'Engaged Units';
    this.top5PopupIcon = 'assets/performance/engaged-units-trophy.svg';
    this.top5PopupUnits = this.apiTop5HseUnits;
    this.top5PopupOpen = true;
  }

  isTop5Item(item: ExRecognitionItem): boolean {
    const label = item.label.toLowerCase().replace(/\s+/g, ' ').trim();
    return label.includes('engaged leadership') || label.includes('engaged hse team');
  }

  openTop5Popup(item: ExRecognitionItem): void {
    this.top5PopupTitle = item.label.replace(/\n/g, ' ');
    this.top5PopupIcon = item.iconSrc;
    this.top5PopupUnits = item.top5 ?? [];
    this.top5PopupOpen = true;
  }

  closeTop5Popup(): void {
    this.top5PopupOpen = false;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────
  ngOnChanges(changes: SimpleChanges) {
    if (changes["isOpen"]) {
      const ionContent = document.querySelector("ion-content.dashboard-wrapper") as HTMLElement;
      if (ionContent) {
        if (this.isOpen) ionContent.style.setProperty("--overflow", "hidden");
        else             ionContent.style.removeProperty("--overflow");
      }
      if (this.isOpen) {
        this.dataSub?.unsubscribe();
        this.dataSub = this.excellenceModalService.modalData$.subscribe((data) => {
          if (!data) return;
          this.hseTierXp             = data.hseTierXp;
          this.perfRawPoints         = data.perfRawPoints ?? 0;
          this.engRawPoints          = data.engRawPoints  ?? 0;
          this.performanceScore      = data.performanceScore;
          this.performanceTotalLabel = data.performanceTotalLabel;
          this.performanceMonths     = data.performanceMonths;
          this.engagementScore       = data.engagementScore;
          this.engagementTotalLabel  = data.engagementTotalLabel;
          this.engagementMonths      = data.engagementMonths;
          this.recognitionItems = data.recognitionItems.map(item => ({
            label:   item.label,
            iconSrc: item.icon || 'assets/performance/award.svg',
            active:  item.active,
            top5:    (item.top5 || []).map((u: any, idx: number) => ({
              rank:      u.rank ?? idx + 1,
              unitName:  u.unitName || u.name || '',
              country:   u.country || '',
              flag:      u.flag || '',
              isCurrent: u.isCurrent === true,
            })),
          }));
          this.achievements = data.achievements.map(a => ({
            label:   a.name,
            xp:      a.xp ?? undefined,
            date:    a.date,
            iconSrc: a.icon,
          }));
          this.selectedCountryFlag = data.selectedCountryFlag || '';
          this.apiTop5HseUnits = (data.top5HseUnits || []).map((u, idx) => ({
            rank:      u.rank ?? idx + 1,
            unitName:  u.unitName || '',
            country:   u.country || '',
            flag:      u.flag || this.selectedCountryFlag,
            isCurrent: u.isCurrent === true,
          }));
        });
      } else {
        this.dataSub?.unsubscribe();
      }
    }
  }

  ngOnDestroy() {
    this.dataSub?.unsubscribe();
  }

  close() { this.closeModal.emit(); }
}
